import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";

import { useTheme } from "../../components/ui-v2";
import { useModelViewerSettings } from "./useModelViewerSettings";

const MODEL_ROTATION_SPEED = 0.0075;
const FRUSTRUM_SCALE = 0.6;

const resizeScene = (
  scene: THREE.Group<THREE.Object3DEventMap>,
  maxExtent: number,
) => {
  // Based on https://stackoverflow.com/a/52271526

  const bbox = new THREE.Box3().setFromObject(scene);
  const cent = bbox.getCenter(new THREE.Vector3());
  const size = bbox.getSize(new THREE.Vector3());

  // Rescale the object to normalized space
  const maxAxis = Math.max(size.x, size.y, size.z);
  scene.scale.multiplyScalar(maxExtent / maxAxis);
  bbox.setFromObject(scene);
  bbox.getCenter(cent);
  bbox.getSize(size);
};

interface ModelProps {
  url: string;
  rotate: boolean;
  showTextured: boolean;
  onModelLoaded?: (model: THREE.Group) => void;
}

export const Model = (props: ModelProps) => {
  const { url, rotate, showTextured, onModelLoaded } = props;

  const { materialMode } = useModelViewerSettings();

  const theme = useTheme();

  const { scene } = useGLTF(url);
  const modelRef = useRef<THREE.Group>(null);

  const clonedScene = useMemo(() => {
    const cloned = SkeletonUtils.clone(scene) as THREE.Group;
    resizeScene(cloned, FRUSTRUM_SCALE);
    return cloned;
  }, [scene]);

  const originalMaterials = useRef<
    Map<THREE.Mesh, THREE.Material | THREE.Material[]>
  >(new Map());

  useEffect(() => {
    originalMaterials.current.clear();

    // Store original materials on first render
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        originalMaterials.current.set(child, child.material);
      }
    });

    // Notify parent that model is loaded
    onModelLoaded?.(clonedScene);
  }, [clonedScene, onModelLoaded]);

  useFrame(() => {
    if (modelRef.current && rotate) {
      modelRef.current.rotation.y += MODEL_ROTATION_SPEED;

      // For skinned meshes, we need to update the world matrix
      // This ensures bone transformations are recalculated
      modelRef.current.updateMatrixWorld(true);
    }
  });

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (materialMode === "textured" || showTextured) {
          // Apply original material
          child.material =
            originalMaterials.current.get(child) || child.material;
          child.material.metalness = 0;
        } else if (materialMode === "plain") {
          // Apply standard material without textures
          child.material = new THREE.MeshStandardMaterial({
            color: theme.palette.secondary[3],
          });
        } else if (materialMode === "wireframe") {
          // Only show wireframes
          child.material = new THREE.MeshBasicMaterial({
            color: theme.palette.secondary[3],
            wireframe: true,
          });
        }
      }
    });
  }, [clonedScene, materialMode, showTextured, theme]);

  return <primitive ref={modelRef} object={clonedScene} />;
};
